import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';
import { Helmet } from 'react-helmet-async';

import { organizationStructuredData, seoConfig, websiteStructuredData } from '@config/seo';
import {
  getLocaleFromPathname,
  getLocaleMetadata,
  resolveLocale,
  supportedLocaleCodes,
  supportedLocales,
  type LocaleCode,
} from '@content';
import { buildCanonicalUrl, ensureAbsoluteUrl, normalizePathname } from '@lib/seo';

const ROBOTS_INDEX_VALUE = 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';
const ROBOTS_NOINDEX_VALUE = 'noindex, nofollow';

type StructuredData = Record<string, unknown>;
type AlternateLocaleLink = {
  hrefLang: string;
  href: string;
};

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string | string[];
  image?: string;
  canonical?: string;
  locale?: LocaleCode | string;
  alternateLinks?: readonly AlternateLocaleLink[];
  type?: string;
  noIndex?: boolean;
  structuredData?: StructuredData | StructuredData[];
}

const toArray = (data?: StructuredData | StructuredData[]) => {
  if (!data) return [];
  return Array.isArray(data) ? data : [data];
};

export function DefaultSeo() {
  const defaultCanonicalUrl = buildCanonicalUrl('/');
  const defaultImageUrl = ensureAbsoluteUrl(seoConfig.defaultImage) ?? seoConfig.defaultImage;
  const defaultStructuredData = useMemo(
    () => [
      {
        ...websiteStructuredData,
        '@id': `${defaultCanonicalUrl}#website`,
      },
      {
        ...organizationStructuredData,
        '@id': `${defaultCanonicalUrl}#organization`,
      },
    ],
    [defaultCanonicalUrl]
  );
  const preloadAssets = seoConfig.preloadAssets ?? [];

  return (
    <Helmet htmlAttributes={{ lang: supportedLocales['en-IN'].hrefLang }}>
      <meta name="description" content={seoConfig.defaultDescription} />
      <meta name="application-name" content={seoConfig.siteName} />
      <meta name="apple-mobile-web-app-title" content={seoConfig.siteName} />
      <meta name="theme-color" content={seoConfig.brandColor} />
      <meta name="format-detection" content="telephone=no" />
      <meta name="referrer" content="strict-origin-when-cross-origin" />
      <meta name="robots" content={ROBOTS_INDEX_VALUE} />
      <meta name="googlebot" content={ROBOTS_INDEX_VALUE} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={seoConfig.siteName} />
      <meta property="og:title" content={seoConfig.defaultTitle} />
      <meta property="og:description" content={seoConfig.defaultDescription} />
      <meta property="og:url" content={defaultCanonicalUrl} />
      <meta property="og:image" content={defaultImageUrl} />
      <meta property="og:image:secure_url" content={defaultImageUrl} />
      <meta property="og:image:alt" content={seoConfig.siteName} />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seoConfig.defaultTitle} />
      <meta name="twitter:description" content={seoConfig.defaultDescription} />
      <meta name="twitter:image" content={defaultImageUrl} />
      <meta name="twitter:image:alt" content={seoConfig.siteName} />
      {preloadAssets.map((asset) => (
        <link
          key={asset.href}
          rel="preload"
          href={asset.href}
          as={asset.as}
          {...(asset.type ? { type: asset.type } : {})}
          {...(asset.crossOrigin ? { crossOrigin: asset.crossOrigin } : {})}
          {...(asset.fetchPriority ? { fetchPriority: asset.fetchPriority } : {})}
        />
      ))}
      {defaultStructuredData.map((schema) => (
        <script key={schema['@id'] as string} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export function SEO({
  title,
  description,
  keywords,
  image,
  canonical,
  locale,
  alternateLinks = [],
  type = 'website',
  noIndex = false,
  structuredData,
}: SEOProps) {
  const location = useLocation();
  const pathname = normalizePathname(location.pathname ?? '/');
  const resolvedLocale = resolveLocale(locale ?? getLocaleFromPathname(pathname));
  const localeMetadata = getLocaleMetadata(resolvedLocale);
  const fullTitle = title
    ? title.toLowerCase().includes(seoConfig.siteName.toLowerCase())
      ? title
      : seoConfig.titleTemplate.includes('%s')
        ? seoConfig.titleTemplate.replace('%s', title)
        : `${title} | ${seoConfig.siteName}`
    : seoConfig.defaultTitle;
  const metaDescription = description ?? seoConfig.defaultDescription;
  const canonicalUrl = buildCanonicalUrl(canonical ?? pathname);
  const imageUrl = ensureAbsoluteUrl(image ?? seoConfig.defaultImage);
  const robotsValue = noIndex ? ROBOTS_NOINDEX_VALUE : ROBOTS_INDEX_VALUE;
  const keywordValue = Array.isArray(keywords) ? keywords.join(', ') : keywords;
  const imageAltText = title ?? seoConfig.siteName;

  const pageStructuredData = useMemo(() => {
    const baseSchema: StructuredData = {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      '@id': `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: fullTitle,
      description: metaDescription,
      isPartOf: {
        '@type': 'WebSite',
        '@id': `${seoConfig.siteUrl}#website`,
      },
      inLanguage: localeMetadata.hrefLang,
    };

    return [baseSchema, ...toArray(structuredData)];
  }, [canonicalUrl, fullTitle, localeMetadata.hrefLang, metaDescription, structuredData]);

  return (
    <>
      <Helmet htmlAttributes={{ lang: localeMetadata.hrefLang, dir: localeMetadata.dir }}>
        <title>{fullTitle}</title>
        <meta name="description" content={metaDescription} />
        {keywordValue && <meta name="keywords" content={keywordValue} />}
        <meta name="robots" content={robotsValue} />
        <meta name="googlebot" content={robotsValue} />
        <link rel="canonical" href={canonicalUrl} />
        {alternateLinks.map((alternate) => (
          <link key={alternate.hrefLang} rel="alternate" hrefLang={alternate.hrefLang} href={alternate.href} />
        ))}

        <meta property="og:type" content={type} />
        <meta property="og:locale" content={localeMetadata.ogLocale} />
        {supportedLocaleCodes
          .filter((candidate) => candidate !== resolvedLocale)
          .map((candidate) => (
            <meta key={candidate} property="og:locale:alternate" content={supportedLocales[candidate].ogLocale} />
          ))}
        <meta property="og:title" content={fullTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:image:secure_url" content={imageUrl} />
        <meta property="og:image:alt" content={imageAltText} />

        <meta name="twitter:title" content={fullTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={imageUrl} />
        <meta name="twitter:image:alt" content={imageAltText} />

        {pageStructuredData.map((schema, index) => (
          <script key={schema['@id']?.toString() ?? index} type="application/ld+json">
            {JSON.stringify(schema)}
          </script>
        ))}
      </Helmet>
    </>
  );
}

export type { SEOProps, StructuredData };
